import { useEffect, useState } from 'react';

import {
  Content,
  ContentHeader,
  SupportButton,
  SupportItem,
  TableColumn,
  useSupportConfig,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { CatalogTable, CatalogTableRow } from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityKindFilter,
  EntityListProvider,
  EntityRefLink,
  EntityTypeFilter,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { capitalize } from '@mui/material/utils';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useNavigateToTemplate, useUser, useUserGroups } from '../../hooks';
import { buildFormData } from '../../utils/formDataBuilder';
import { isEntityManagedBy } from '../../utils/tenantAccess';

import { KaaPTenantFilters } from './filters';
import { toKaaPTenant } from '../../utils/kaapTenants';
import { toastApiRef } from '@backstage/frontend-plugin-api';

const INITIAL_KIND = 'resource';
const TENANT_TYPE = 'kaap-tenant';

function getSupportItems(items: SupportItem[]): SupportItem[] {
  return [
    {
      title: 'Find us in Slack',
      links: [
        {
          title: '#pe-kaap',
          url: 'https://tomtomslack.slack.com/archives/C0746A4T0B1',
        },
      ],
    },
    {
      title: 'KaaP documentation',
      links: [
        {
          title: 'What is KaaP?',
          url: 'https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation',
        },
      ],
    },
    ...items,
  ];
}

const columns: TableColumn<CatalogTableRow>[] = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  {
    title: 'ID',
    field: 'entity.spec.tenant.id',
    width: '70px',
    render: ({ entity }) => (entity?.spec?.tenant as any)?.id ?? '—',
  },
  {
    title: 'Name',
    field: 'entity.metadata.name',
    width: '220px',
    render: ({ entity }) => (
      <EntityRefLink
        entityRef={entity}
        defaultKind={INITIAL_KIND}
        title={entity.metadata.title || entity.metadata.name}
      />
    ),
  },
  {
    title: 'Stage & Tier',
    field: 'entity.spec.tenant.stage',
    width: '160px',
    render: ({ entity }) => {
      const tenant = (entity?.spec?.tenant as any) || {};
      const stage = tenant.stage ? capitalize(String(tenant.stage)) : undefined;
      const tier = tenant.tier ? capitalize(String(tenant.tier)) : undefined;
      return [stage, tier].filter(Boolean).join(' ') || '—';
    },
  },
  {
    title: 'Cluster',
    field: 'entity.spec.tenant.clusterId',
    width: '220px',
    render: ({ entity }) =>
      (entity?.spec?.tenant as any)?.clusterId?.split('/')[1] ?? '—',
  },
  CatalogTable.columns.createOwnerColumn(),
];

const NO_TENANT_ACCESS_TOOLTIP =
  'No access — you must belong to a tenant security group or be an admin';

const KaaPTenantExplorerContent = () => {
  const { updateFilters } = useEntityList();
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { items: supportItems } = useSupportConfig();
  const { isAdmin } = useUser();
  const userGroups = useUserGroups();
  const alertApi = useApi(toastApiRef);
  const navigateToTemplate = useNavigateToTemplate();
  const [editingTenant, setEditingTenant] = useState<string | null>(null);

  const hasTenantAccess = (row: CatalogTableRow): boolean =>
    isAdmin || isEntityManagedBy(userGroups.groupIds, row.entity);

  const handleEditClick = async (row: CatalogTableRow) => {
    const tenantName = row.entity.metadata.name;
    setEditingTenant(tenantName);
    try {
      const tenant = toKaaPTenant(row.entity);
      navigateToTemplate({
        formData: buildFormData(tenant),
        action: 'edit',
      });
    } catch {
      alertApi.post({
        title: 'Error',
        description: `Failed to load tenant data for "${tenantName}". Please try again.`,
        status: 'danger',
      });
    } finally {
      setEditingTenant(null);
    }
  };

  // Pin the list to KaaP tenant resources. Setting the type filter directly
  // (rather than via useEntityTypeFilter) keeps it stable — the hook's facet
  // reconciliation can otherwise drop the selection and leak other resource
  // types such as subscriptions into the table.
  useEffect(() => {
    updateFilters({
      kind: new EntityKindFilter(INITIAL_KIND, capitalize(INITIAL_KIND)),
      type: new EntityTypeFilter([TENANT_TYPE]),
    });
  }, [updateFilters]);

  // Edit/Delete are gated on Azure AD security-group membership (matched
  // against the tenant's spec.tenant.securityObjectIds) or KaaP admin, and
  // launch the corresponding scaffolder template with the tenant prefilled.
  const actions = [
    (row: CatalogTableRow) => {
      const hasAccess = hasTenantAccess(row);
      const isLoading = editingTenant === row.entity.metadata.name;
      const disabled = editingTenant !== null || !hasAccess;
      return {
        icon: () => (
          <EditIcon fontSize="small" style={{ opacity: disabled ? 0.4 : 1 }} />
        ),
        tooltip: (() => {
          if (isLoading) return 'Loading…';
          return hasAccess ? 'Edit tenant' : NO_TENANT_ACCESS_TOOLTIP;
        })(),
        disabled,
        onClick: () => handleEditClick(row),
      };
    },
    (row: CatalogTableRow) => {
      const hasAccess = hasTenantAccess(row);
      return {
        icon: () => (
          <DeleteIcon
            fontSize="small"
            style={{ opacity: hasAccess ? 1 : 0.4 }}
          />
        ),
        tooltip: hasAccess ? 'Delete tenant' : NO_TENANT_ACCESS_TOOLTIP,
        disabled: !hasAccess,
        onClick: () =>
          navigateToTemplate({
            formData: { tenantFullName: row.entity.metadata.name },
            action: 'delete',
          }),
      };
    },
    (row: CatalogTableRow) => {
      const starred = isStarredEntity(row.entity);
      return {
        icon: () =>
          starred ? (
            <StarIcon style={{ color: '#f3ba37' }} />
          ) : (
            <StarBorderIcon />
          ),
        tooltip: starred ? 'Remove from favorites' : 'Add to favorites',
        onClick: () => toggleStarredEntity(row.entity),
      };
    },
  ];

  return (
    <Content>
      <ContentHeader title="">
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigateToTemplate({ formData: {}, action: 'create' })}
        >
          Create tenant
        </Button>
        <SupportButton items={getSupportItems(supportItems)}>
          KaaP tenants
        </SupportButton>
      </ContentHeader>

      <CatalogFilterLayout>
        <CatalogFilterLayout.Filters>
          <Box style={{ minWidth: '240px', margin: '-20px' }}>
            <KaaPTenantFilters isAdmin={isAdmin} userGroups={userGroups} />
          </Box>
        </CatalogFilterLayout.Filters>

        <CatalogFilterLayout.Content>
          <CatalogTable
            columns={columns}
            tableOptions={{ showTitle: false, tableLayout: 'fixed' }}
            actions={actions}
          />
        </CatalogFilterLayout.Content>
      </CatalogFilterLayout>
    </Content>
  );
};

export const KaaPTenantExplorerPage = () => (
  <EntityListProvider>
    <KaaPTenantExplorerContent />
  </EntityListProvider>
);
